import { StorageAdapter } from '../storage/StorageAdapter'

/**
 * sync/prune.js — poda de filas `done` del outbox (higiene de crecimiento, deuda de tuning 2026-06).
 *
 * Sin poda, el outbox crece sin cota: cada respuesta/addon/firma/cierre deja su fila `done` para
 * siempre, y los lookups por dueño (purga de fotos, guard LWW) degradan con semanas de capturas.
 * Con poda, "sincronizado" sigue siendo ausencia de pending/in_flight (useSyncStatus no cuenta done).
 *
 * QUE SE PODA — SOLO filas status='done' que cumplan AMBAS:
 *   (a) ANTIGÜEDAD: created_at < now - retentionMs (default 7 dias) — deja ventana de auditoria
 *       reciente para diagnostics y para inspeccion manual post-sync.
 *   (b) GUARD DE FOTOS: el predicado de purga de photos/purge.js exige que el parent tenga AL MENOS
 *       una fila outbox 'done' (= subida confirmada) para poder borrar el binario. Si podaramos el
 *       done ANTES de que la foto se purgue, la foto quedaria huerfana en disco PARA SIEMPRE (nunca
 *       satisface el predicado). Por eso: NO se poda un done cuyo client_uuid aun tenga photo_meta
 *       con upload_status != 'done'.
 *
 * QUE NO SE PODA (decision anti-perdida):
 *   - pending / in_flight: trabajo vivo, jamas.
 *   - dead: es la UNICA evidencia visible de una captura que NO llego al servidor (useSyncStatus la
 *     cuenta, dead-letter.js la lista y el operador decide). Borrarla por tiempo = perdida silenciosa.
 *
 * Idempotente y best-effort: un DELETE acotado; un fallo se reintenta en la proxima ventana. SQL SOLO
 * via el `db` inyectable (STOR-05). El indice idx_outbox_client (migracion v3) cubre el subquery del
 * guard; idx_outbox_status no aplica aqui pero el volumen de done viejos es el que esta poda acota.
 */

export const PRUNE_RETENTION_MS = 7 * 24 * 3600000 // 7 dias
const PRUNE_INTERVAL_MS = 6 * 3600000 // correr a lo mas cada 6h (via pruneOutboxThrottled)

/**
 * Borra las filas `done` mas viejas que la retencion cuyo parent ya no las necesita para la purga
 * de fotos. `now` inyectable para tests.
 *
 * @param {{ db?: object, now?: number, retentionMs?: number }} opts
 * @returns {Promise<{ pruned: number }>}
 */
export async function pruneOutbox ({ db = StorageAdapter, now = Date.now(), retentionMs = PRUNE_RETENTION_MS } = {}) {
  const res = await db.run(
    "DELETE FROM outbox WHERE status='done' AND created_at < ? " +
    "AND NOT EXISTS (SELECT 1 FROM photo_meta pm WHERE pm.parent_uuid = outbox.client_uuid AND pm.upload_status != 'done')",
    [now - retentionMs],
    true
  )
  const pruned = (res && res.changes && res.changes.changes) || 0
  return { pruned }
}

// Throttle a nivel modulo: el tick del worker corre cada 30s; la poda no necesita correr mas de una
// vez por ventana (PRUNE_INTERVAL_MS). JS single-threaded -> basta el timestamp.
let _lastPruneAt = 0

/** Reset del throttle (tests). */
export function _resetPruneThrottle () { _lastPruneAt = 0 }

/**
 * Variante con throttle para el tick del sync-worker: corre pruneOutbox a lo mas cada
 * PRUNE_INTERVAL_MS; fuera de ventana es un no-op barato ({skipped:true}).
 */
export async function pruneOutboxThrottled ({ db = StorageAdapter, now = Date.now() } = {}) {
  if (now - _lastPruneAt < PRUNE_INTERVAL_MS) return { skipped: true }
  _lastPruneAt = now
  return pruneOutbox({ db, now })
}
